import {
  addCpfMask,
  PROFILE_URL_PATH,
  removeCpfMask,
  removeTelephoneCountryCode,
  STATUSPAGE,
} from '@repo/ui';
import {
  CreateProfessionalManagementUseCaseInterface,
  GetAddressViaZipCodeUseCaseInterface,
  GetAllCitiesForSelectOptionsUseCaseInterface,
  GetByIdProfessionalManagementUseCaseInterface,
  GetListProfileUseCaseInterface,
  UpdateProfessionalManagementUseCaseInterface,
  UpdateStatusAssociatePersonProfileInterface,
} from 'domain/usecases';
import {
  getProfileNameFromProfileType,
  getProfileTypeFromName,
} from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearCitiesForSelectOptionsData,
  loadCitiesForSelectOptionsData,
} from 'presentation/config/store/cities/cities-slice';
import { loadProfessionalManagementById } from 'presentation/config/store/professionals/profissional-management-slice';
import { loadProfilesAssociated } from 'presentation/config/store/users/users-associated-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

export const useProfessionalManagement = () => {
  //states
  const [searchZipCodeInputLoading, setSearchZipCodeInputLoading] = useState(
    STATUSPAGE.DONE,
  );
  const [searchCityInputLoading, setSearchCityInputLoading] = useState(
    STATUSPAGE.DONE,
  );
  const [profileAssociateId, setProfileId] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<boolean | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [profilesLoading, setProfilesLoading] = useState<boolean>(false);

  //hooks
  const dispatch = useDispatch();
  const { professionalService } = useService();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { professionalType, id } = useParams<{
    professionalType: PROFILE_URL_PATH;
    id: string;
  }>();
  const { locationsService, usersService } = useService();

  //utils
  function removeHyphenFromCep(cep) {
    return cep.replace('-', '');
  }

  //redux
  const { professionalManagementById: professionalManagementByIdResponse } =
    useAppSelector((state) => state.professionalManagementById);

  //validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: professionalManagementByIdResponse?.name || '',
      cpf: professionalManagementByIdResponse?.cpf
        ? addCpfMask(`${professionalManagementByIdResponse?.cpf}`)
        : addCpfMask(state?.value),
      socialName: professionalManagementByIdResponse?.socialName || undefined,
      gender: professionalManagementByIdResponse
        ? {
            label:
              professionalManagementByIdResponse?.gender === 'M'
                ? 'Masculino'
                : 'Feminino',
            value: professionalManagementByIdResponse?.gender,
          }
        : null,

      birthDate: professionalManagementByIdResponse?.birthDate || '',
      profession: {
        label: professionalManagementByIdResponse?.profession?.value,
        value: professionalManagementByIdResponse?.profession?.label,
      },

      professionalCouncil:
        professionalManagementByIdResponse?.professionalCouncil || '',
      phone: `${'+55' + professionalManagementByIdResponse?.phone}`,
      email: professionalManagementByIdResponse?.email || '',
      zipcode: professionalManagementByIdResponse?.zipcode || '',
      street: professionalManagementByIdResponse?.street || '',
      addressNumber: professionalManagementByIdResponse?.addressNumber || '',
      addressComplement:
        professionalManagementByIdResponse?.addressComplement || '',
      district: professionalManagementByIdResponse?.district || '',
      city: professionalManagementByIdResponse?.city
        ? {
            label: professionalManagementByIdResponse?.city?.name,
            value: professionalManagementByIdResponse?.city?.id,
          }
        : null,

      employmentMode: professionalManagementByIdResponse?.employmentMode
        ? {
            label:
              professionalManagementByIdResponse?.employmentMode?.name === 'PJ'
                ? 'PJ'
                : 'CLT',
            value: professionalManagementByIdResponse?.employmentMode?.name,
          }
        : null,

      weekWorkload: professionalManagementByIdResponse?.weekWorkload || '',
      valueHour: professionalManagementByIdResponse?.valueHour || '',
      validity: professionalManagementByIdResponse?.validity || undefined,
      hasAccessToTimeControl:
        professionalManagementByIdResponse?.hasAccessToTimeControl
          ? {
              label: professionalManagementByIdResponse?.hasAccessToTimeControl
                .label
                ? 'Sim'
                : 'Não',
              value:
                professionalManagementByIdResponse?.hasAccessToTimeControl
                  .value,
            }
          : null,
      setNewPass: professionalManagementByIdResponse?.setNewPass || '',
      isActive: professionalManagementByIdResponse?.isActive
        ? {
            label:
              professionalManagementByIdResponse?.isActive === true
                ? 'Sim'
                : 'Não',
            value: professionalManagementByIdResponse?.isActive,
          }
        : null,
      manager: professionalManagementByIdResponse?.manager
        ? {
            label:
              professionalManagementByIdResponse?.manager === true
                ? 'Sim'
                : 'Não',
            value: professionalManagementByIdResponse?.manager,
          }
        : null,
    },
    validationSchema: Yup.object().shape({
      cpf: Yup.string().required('CPF é obrigatório').cpfType('CPF é inválido'),
      name: Yup.string().required('Campo é obrigatório.'),
      gender: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable()
        .required('Campo é obrigatório.'),
      city: Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      }),
      employmentMode: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      hasAccessToTimeControl: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      manager: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),
      setNewPass: Yup.string().nullable(),
      birthDate: Yup.string().required('Campo é obrigatório.'),
      phone: Yup.string().required('Campo é obrigatório.'),
      email: Yup.string().required('Campo é obrigatório.'),
      zipcode: Yup.string().required('Campo é obrigatório.'),
      street: Yup.string().required('Campo é obrigatório.'),
      addressNumber: Yup.string().required('Campo é obrigatório.'),
      district: Yup.string().required('Campo é obrigatório.'),
    }),
    onSubmit: (values) => {
      const valuesToSubmit = {
        cpf: removeCpfMask(values.cpf),
        name: values.name,
        socialName: values.socialName,
        isActive: values?.isActive?.value,
        birthDate: values.birthDate,
        profession: values?.profession?.value,
        professionalCouncil: values.professionalCouncil,
        phone: removeTelephoneCountryCode(values.phone),
        email: values.email,
        addressComplement: values.addressComplement,
        addressNumber: values.addressNumber,
        city: Number(values.city?.value),
        district: values.district,
        employmentMode: String(values?.employmentMode?.value),
        gender: values?.gender?.value,
        street: values.street,
        validity: values.validity,
        manager: Boolean(values.manager?.value),
        valueHour: Number(values.valueHour),
        weekWorkload: Number(values.weekWorkload),
        zipcode: removeHyphenFromCep(values.zipcode),
        professionalType: getProfileTypeFromName(professionalType),
        hasAccessToTimeControl: values?.hasAccessToTimeControl?.value,
        setNewPass: values.setNewPass,
      };
      if (id) {
        handleUpdateProfessionalManagement({
          id: id,
          ...valuesToSubmit,
        });
      } else {
        handleCreateProfessionalManagement(valuesToSubmit);
      }
    },
  });

  const clearAddressInputValues = useCallback(() => {
    validation.setFieldValue('address', '');
    validation.setFieldValue('district', '');
    validation.setFieldValue('addressComplement', '');
    validation.setFieldValue('city', null);
  }, []);

  const autoFillAddressInputValuesAccordinglyToZipCodeAPI = useCallback(
    ({
      logradouro,
      bairro,
      complemento,
      ibge,
      localidade,
    }: GetAddressViaZipCodeUseCaseInterface.Result) => {
      validation.setFieldValue('street', logradouro || '');
      validation.setFieldValue('district', bairro || '');
      validation.setFieldValue('addressComplement', complemento || '');
      validation.setFieldValue('city', {
        label: localidade,
        value: ibge,
      });
    },
    [],
  );

  function toggleModalActivateInactiveProfile() {
    setIsModalOpen(!isModalOpen);
  }

  const handleActiveInactiveSwitchChangeProfile = (
    id: string,
    status: boolean,
    roleId: number,
  ) => {
    setProfileId(id);
    setProfileStatus(status);
    setRoleId(roleId);
    toggleModalActivateInactiveProfile();
  };

  const handleUpdateProfessionalManagement = async (
    params: UpdateProfessionalManagementUseCaseInterface.Params,
  ) => {
    return await professionalService
      .updateProfessionalManagement(params)
      .then((res: UpdateProfessionalManagementUseCaseInterface.Result) => {
        notifySuccess(res.message || 'Profissional atualizado com sucesso.');
        navigate(
          `/equipe/${getProfileNameFromProfileType(params.professionalType)}`,
        );
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao atualizar um profissional.');
      });
  };

  const handleCreateProfessionalManagement = async (
    params: CreateProfessionalManagementUseCaseInterface.Params,
  ) => {
    return await professionalService
      .createProfessionalManagement(params)
      .then((res: CreateProfessionalManagementUseCaseInterface.Result) => {
        notifySuccess(res.message || 'Profissional criado com sucesso.');
        navigate(
          `/equipe/${getProfileNameFromProfileType(params.professionalType)}`,
        );
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao criar um profissional.');
      });
  };

  const handleGetByIdProfessionalManagement = async (
    params: GetByIdProfessionalManagementUseCaseInterface.Params,
  ) => {
    return await professionalService
      .getByIdProfessionalManagement(params)
      .then((res: GetByIdProfessionalManagementUseCaseInterface.Result) => {
        dispatch(loadProfessionalManagementById(res));
      })
      .catch((err) => {
        notifyError(err.message || 'Erro ao buscar um profissional.');
      });
  };

  const requestGetAddressViaZipCode = useCallback(
    (params: GetAddressViaZipCodeUseCaseInterface.Params) => {
      if (!params) return;
      setSearchZipCodeInputLoading(STATUSPAGE.LOADING);
      locationsService
        .getAddressViaZipCode(params)
        .then((res) => {
          if (res.erro) {
            clearAddressInputValues();
            throw new Error('CEP inexistente');
          } else {
            autoFillAddressInputValuesAccordinglyToZipCodeAPI(res);
          }
        })
        .catch((err) => {
          notifyError(err.message || 'Algo inesperado aconteceu');
        })
        .finally(() => setSearchZipCodeInputLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const requestGetAllCitiesForSelectOptions = useCallback(
    async (params: GetAllCitiesForSelectOptionsUseCaseInterface.Params) => {
      setSearchCityInputLoading(STATUSPAGE.LOADING);
      await locationsService
        .getAllCitiesForSelectOptions(params)
        .then((res) => dispatch(loadCitiesForSelectOptionsData(res)))
        .catch((err) => {
          dispatch(clearCitiesForSelectOptionsData());
          notifyError(err);
        })
        .finally(() => setSearchCityInputLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const requestGetAllProfilesAssociated = useCallback(
    async (params?: GetListProfileUseCaseInterface.Params) => {
      await usersService
        .getListProfile(params)
        .then((res: GetListProfileUseCaseInterface.Result) => {
          dispatch(loadProfilesAssociated(res));
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao buscar perfis associados.');
        });
    },
    [],
  );

  const requestUpdateStatusAssociatePersonProfile = useCallback(
    async (params: UpdateStatusAssociatePersonProfileInterface.Params) => {
      return await usersService
        .updateStatusAssociatePersonProfile(params)
        .then((res: UpdateStatusAssociatePersonProfileInterface.Result) => {
          notifySuccess(res.message || 'Status alterado com sucesso.');
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao alterar status.');
        });
    },
    [],
  );

  return {
    professionalManagementByIdResponse,
    validation,
    searchCityInputLoading,
    searchZipCodeInputLoading,
    profileAssociateId,
    profileStatus,
    roleId,
    isModalOpen,
    profilesLoading,
    setProfilesLoading,
    setProfileStatus,
    setProfileId,
    requestGetAddressViaZipCode,
    requestGetAllCitiesForSelectOptions,
    handleUpdateProfessionalManagement,
    handleCreateProfessionalManagement,
    handleGetByIdProfessionalManagement,
    clearAddressInputValues,
    autoFillAddressInputValuesAccordinglyToZipCodeAPI,
    requestGetAllProfilesAssociated,
    requestUpdateStatusAssociatePersonProfile,
    toggleModalActivateInactiveProfile,
    handleActiveInactiveSwitchChangeProfile,
  };
};
